import { Box, Stack, TextField, Typography } from "@mui/material";
import AudioPreview from "components/audio/AudioPreview";
import Modal from "components/modal/Modal";
import ModalSubmitFooter from "components/modal/ModalSubmitFooter";
import Select from "components/select/Select";
import { useFormik } from "formik";
import { useState } from "react";
import { IvrApi } from "service/api/ivr.api";
import { useGetListProvince } from "service/query/common.query";
import { useCreateIvr, useEditIvr } from "service/query/ivr.query";
import { getSelectFieldProps, getTextFieldProps } from "ultis/form";
import * as Yup from "yup";

const IvrModal = ({ open, onClose, currentItem }) => {
  const { data: listProvince } = useGetListProvince({
    filter: { where: { isCskh: true } },
  });
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");

  const createIvrQuery = useCreateIvr();
  const editIvrQuery = useEditIvr(currentItem?.id);

  const handleSaveQueue = async ({ name, subOrg, fileS3Url, content }) => {
    if (!file && !fileS3Url) {
      setFileError("Không được để trống");
      return;
    }

    setLoading(true);
    const payload = {
      name,
      subOrg,
      content,
      fileS3Url,
    };
    if (file) {
      try {
        const formData = new FormData();
        formData.append("file", file);
        const uploadedFileRes = await IvrApi.uploadIvr(formData);
        payload.fileS3Url = uploadedFileRes.data.fileS3Url;
      } catch (error) {
        setFileError("Tải file lên thất bại");
        setFile(null);
        setLoading(false);
        return;
      }
    }

    if (currentItem) {
      editIvrQuery.mutate(
        {
          ...currentItem,
          ...payload,
        },
        {
          onSuccess: onClose,
          onSettled: () => {
            setLoading(false);
          },
        }
      );
    } else {
      createIvrQuery.mutate(payload, {
        onSuccess: onClose,
        onSettled: () => {
          setLoading(false);
        },
      });
    }
  };

  const formik = useFormik({
    initialValues: {
      name: currentItem?.name || "",
      subOrg: currentItem?.subOrg || "",
      fileS3Url: currentItem?.fileS3Url || "",
      content: currentItem?.content || "",
    },
    validationSchema: Yup.object().shape({
      subOrg: Yup.string().required("Không được để trống"),
      name: Yup.string().required("Không được để trống"),
    }),

    onSubmit: handleSaveQueue,
  });

  const handleUploadAudioFile = async (event) => {
    const audioFile = event.target?.files?.[0];
    if (audioFile) {
      setFile(audioFile);
    } else {
      setFile(null);
    }
    setFileError("");
  };

  return (
    <Modal
      open={open}
      title={currentItem ? "Chỉnh sửa file IVR" : "Thêm file IVR"}
      onClose={onClose}
      maxWidth="md">
      <form onSubmit={formik.handleSubmit}>
        <Stack spacing={2}>
          <Select
            {...getSelectFieldProps(formik, "subOrg")}
            label="Bộ phận"
            options={listProvince?.map((item) => ({
              label: item.name,
              value: item.code,
            }))}
            allowClear
          />
          <TextField
            {...getTextFieldProps(formik, "name")}
            label="Tên file"
            variant="outlined"
            fullWidth
          />
          {formik.values.fileS3Url && !file && (
            <audio src={formik.values.fileS3Url} controls autoPlay={false} />
          )}
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography component="span" fontSize={14}>
              Upload file âm thanh
            </Typography>
            <Box
              component="label"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 100,
                height: 30,
                color: (theme) => theme.palette.info.contrastText,
                bgcolor: (theme) => theme.palette.info.main,
                cursor: "pointer",
                borderRadius: "4px",
              }}>
              Chọn tệp
              <input
                accept="audio/wav, audio/mp3, video/mp4"
                onChange={handleUploadAudioFile}
                type="file"
                hidden
              />
            </Box>
            {file && (
              <Typography fontSize={14} component="span">
                {file.name}
              </Typography>
            )}

            {fileError && (
              <Typography
                color={(theme) => theme.palette.error.main}
                fontSize={12}>
                {fileError}
              </Typography>
            )}
          </Stack>
          {file && <AudioPreview file={file} />}
          <TextField
            {...getTextFieldProps(formik, "content")}
            label="Nội dung"
            multiline
            rows={4}
          />
        </Stack>

        <ModalSubmitFooter onCancel={onClose} loading={loading} />
      </form>
    </Modal>
  );
};

export default IvrModal;
